import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ChevronRight, Sparkles, Eye, ArrowRight,
  Plane, Building2, HardHat, Factory, Hospital, GraduationCap,
  Landmark, Ship, Train, Truck, Warehouse, Home, TreePine,
  Microscope, Laptop, Globe, Rocket, Shield, Heart, Zap,
  Mountain, Waves, Sun, Wind, Atom, Briefcase, Stethoscope,
  Gavel, Scale, Coins, Store, MapPin, Target, Award, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Plane, Building2, HardHat, Factory, Hospital, GraduationCap,
  Landmark, Ship, Train, Truck, Warehouse, Home, TreePine,
  Microscope, Laptop, Globe, Rocket, Shield, Heart, Zap,
  Mountain, Waves, Sun, Wind, Atom, Briefcase, Stethoscope,
  Gavel, Scale, Coins, Store, MapPin, Target, Award,
};

function FloatingOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#5AA2AE]/5 animate-pulse" style={{ animationDuration: '4s' }} />
      <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-[#5AA2AE]/8 animate-pulse" style={{ animationDuration: '6s' }} />
      <div className="absolute top-1/2 right-1/4 w-32 h-32 rounded-full bg-amber-500/5 animate-pulse" style={{ animationDuration: '5s' }} />
    </div>
  );
}

export default function ExperiencePulseModal({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    fetch(`${API_URL}/api/experience-pulse/projects`, { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        setProjects(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false))
      .finally(() => clearTimeout(timeout));
    return () => { controller.abort(); clearTimeout(timeout); };
  }, [open]);

  const goTo = (next) => {
    if (animating || next === step) return;
    setDirection(next > step ? 1 : -1);
    setAnimating(true);
    setTimeout(() => { setStep(next); setAnimating(false); }, 200);
  };

  const handleSelectProject = async (project) => {
    // Track access
    try {
      await fetch(`${API_URL}/api/experience-pulse/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: project.project_id }),
      });
    } catch {}
    onClose();
    navigate(`/public/${project.project_id}`);
  };

  const slideClass = animating
    ? direction > 0 ? 'opacity-0 translate-x-6' : 'opacity-0 -translate-x-6'
    : 'opacity-100 translate-x-0';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden border-0 rounded-2xl" data-testid="experience-pulse-modal">
        <div className="relative min-h-[480px] flex flex-col">
          <FloatingOrbs />

          {/* Header bar */}
          <div className="relative z-10 flex items-center justify-between px-6 pt-5 pb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#5AA2AE]/10 flex items-center justify-center">
                <Eye className="w-4 h-4 text-[#5AA2AE]" />
              </div>
              <span className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Experience Pulse</span>
            </div>
            <div className="flex gap-1.5">
              {[0, 1].map(i => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`rounded-full transition-all ${i === step ? 'w-6 h-2 bg-[#5AA2AE]' : 'w-2 h-2 bg-slate-200 hover:bg-slate-300'}`}
                  data-testid={`experience-pulse-dot-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className={`relative z-10 flex-1 px-6 pb-6 transition-all duration-200 ease-out ${slideClass}`}>
            {step === 0 ? (
              /* Slide 1: Introduction */
              <div className="flex flex-col items-center text-center pt-4" data-testid="experience-pulse-intro">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5AA2AE]/20 to-[#5AA2AE]/5 flex items-center justify-center mb-5 shadow-sm">
                  <Sparkles className="w-8 h-8 text-[#5AA2AE]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  See Pulse in Action
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed max-w-md mb-4">
                  Experience Pulse lets you see what a real user experiences. Select a demo project
                  to explore — you'll answer a few sample questions and enter your contact details.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed max-w-md mb-4">
                  Once you're done, you'll receive an invitation to log in and explore the full application —
                  About Me, About Others, dashboards, reports, and AI-powered insights.
                </p>
                <div className="bg-[#5AA2AE]/5 border border-[#5AA2AE]/15 rounded-xl px-4 py-3 max-w-sm">
                  <p className="text-xs text-[#5AA2AE] font-medium">
                    This is a demo project — don't worry about answering questions realistically.
                    Just explore how Pulse works!
                  </p>
                </div>

                <Button
                  onClick={() => goTo(1)}
                  className="mt-6 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-2 h-11 px-6"
                  data-testid="experience-pulse-next-btn"
                >
                  Choose a Demo Project
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              /* Slide 2: Project Selection */
              <div className="pt-2" data-testid="experience-pulse-projects">
                <div className="text-center mb-5">
                  <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Choose a Demo Project
                  </h2>
                  <p className="text-sm text-slate-500">Select a project to explore the Pulse experience</p>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[#5AA2AE]" />
                  </div>
                ) : projects.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm text-slate-500">No demo projects available yet. Check back soon!</p>
                  </div>
                ) : (
                  <div className="grid gap-3 max-h-[300px] overflow-y-auto pr-1">
                    {projects.map((project, i) => {
                      const IconComp = ICON_MAP[project.icon] || Building2;
                      return (
                        <button
                          key={project.project_id}
                          onClick={() => handleSelectProject(project)}
                          className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-100 bg-white
                            hover:border-[#5AA2AE]/40 hover:bg-[#5AA2AE]/3 hover:shadow-md
                            transition-all duration-200 text-left group"
                          style={{ animationDelay: `${i * 80}ms` }}
                          data-testid={`demo-project-card-${project.project_id}`}
                        >
                          <div className="w-11 h-11 rounded-xl bg-[#5AA2AE]/10 flex items-center justify-center flex-shrink-0
                            group-hover:bg-[#5AA2AE]/20 transition-colors">
                            <IconComp className="w-5 h-5 text-[#5AA2AE]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-slate-900 mb-0.5 group-hover:text-[#5AA2AE] transition-colors">
                              {project.display_title}
                            </h3>
                            <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                              {project.short_description}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#5AA2AE] mt-1 flex-shrink-0 transition-colors" />
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-start mt-4">
                  <Button variant="ghost" size="sm" onClick={() => goTo(0)} className="text-slate-400 gap-1">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
